import { filterInstrument } from './pad-config.service.utils';
import { FORWARD_TYPE, SPOT_PRICE_TYPE, StateMutation } from '.';
import { getCurrenciesFromCcyPair } from '@alk/currency';
import { PadConfigExtended } from '../../interfaces';
import { Tenor } from '@alk/dates';

/**
 * State mutation accumulator. Spot type should be always started
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const priceStarted: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) =>
    (Boolean(changeSet.productType) && changeSet.productType !== accumulatedValue.productType) ?
        { ...changeSet, isStartRequested: SPOT_PRICE_TYPE.includes(changeSet.productType) } :
        changeSet;

/**
 * State mutation accumulator. Stop forward\swap feed on changes
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const forwardStopOnChanges: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) =>
    (
        Boolean(changeSet) &&
        accumulatedValue.isStartRequested &&
        !changeSet.isStartRequested &&
        FORWARD_TYPE.includes(accumulatedValue.productType)
    ) ?
        { ...changeSet, isStartRequested: false } :
        changeSet;

/**
 * State mutation accumulator: for spot date should be SP
 * @param _accumulatedValue previous state
 * @param changeSet new changes
 */
export const dateForSpot: StateMutation<PadConfigExtended> = (
    _accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) =>
    (changeSet.productType === 'spot')  ?
        { ...changeSet, date: 'SP' as Tenor } :
        changeSet;

/**
 * State mutation accumulator: filter instruments by selected product type
 * @param _accumulatedValue previous state
 * @param changeSet new changes
 */
export const filterInstruments: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) =>
    Boolean(changeSet.allInstruments) || (Boolean(changeSet.productType) && changeSet.productType !== accumulatedValue.productType)  ?
        {
            ...changeSet,
            instruments: (changeSet.allInstruments ?? accumulatedValue.allInstruments)?.filter(
                filterInstrument(changeSet.productType ?? accumulatedValue.productType),
            ) ?? [],
        } :
        changeSet;

/**
 * State mutation accumulator. On instruments list update, the current instrument should be updated
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const updateInstrumentOnInstrumentListChanged: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    let result = changeSet;
    if (Boolean(changeSet.instruments) && Boolean(accumulatedValue.instrument)) {
        const updatedCurrentInstrument =
            changeSet.instruments.find(instrument => instrument.symbol === accumulatedValue.instrument.symbol);

        if (Boolean(updatedCurrentInstrument)) {
            result = { ...changeSet, instrument: updatedCurrentInstrument };
        }
    }
    return result;
};

/**
 * State mutation accumulator. On Instruments instrument should first one
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const defaultInstrument: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    if (Boolean(changeSet.instrument || accumulatedValue.instrument)) {
        return changeSet;
    }

    const defaultInstrument = changeSet.instruments?.find(instrument => Boolean(instrument?.isDefault));

    return {
        ...changeSet,
        instrument: defaultInstrument ?? changeSet.instruments?.[ 0 ],
    };
};

/**
 * State mutation accumulator. Should populate base currency
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const defaultBaseCurrency: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {

    const result = { ...changeSet };

    const instrument = changeSet.instrument ?? accumulatedValue.instrument;
    const currency = changeSet.tradedCurrency ?? accumulatedValue.tradedCurrency;

    if (Boolean(instrument)) {
        const { currencyQuote, currencyBase } = getCurrenciesFromCcyPair(instrument.symbol);
        if (currency !== currencyQuote && currency !== currencyBase) {
            result.tradedCurrency = currencyBase;
        }
    }
    return result;

};

/**
 * State mutation accumulator. On change symbol of instrument qty should reset
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const resetQtyOnInstrumentChanged: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    return Boolean(changeSet.instrument) &&
        Boolean(accumulatedValue.instrument) &&
        changeSet.instrument.symbol !== accumulatedValue.instrument.symbol ?
        {
            ...changeSet,
            quantity: undefined,
            quantitySecondLeg: undefined,
        } :
        changeSet;
};

/**
 * State mutation accumulator. On product change the traded currency should be reset to the base currency
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const resetTradedCcyOnProductChanged: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    const instrument = changeSet.instrument ?? accumulatedValue.instrument;
    let currencyBase;

    if (Boolean(instrument)) {
        const currencies = getCurrenciesFromCcyPair(instrument.symbol);
        currencyBase = currencies.currencyBase;
    }
    return Boolean(changeSet.productType) &&
        Boolean(accumulatedValue.productType) &&
        Boolean(Boolean(instrument)) &&
        changeSet.productType !== accumulatedValue.productType ?
        { ...changeSet, tradedCurrency: currencyBase } :
        changeSet;
};

/**
 * State mutation accumulator. On change symbol of instrument pending parameters
 * selectedTradeSide, triggerPrice should be reset. Qty is reset for every order type.
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const resetPendingParamsOnInstrumentChanged: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    return changeSet.productType === 'pending' &&
        Boolean(changeSet.instrument) &&
        Boolean(accumulatedValue.instrument) &&
        changeSet.instrument.symbol !== accumulatedValue.instrument.symbol ?
        {
            ...changeSet,
            selectedTradeSide: undefined,
            triggerPrice: undefined,
            selectedTradeSideSecondLeg: undefined,
            triggerPriceSecondLeg: undefined,
        } :
        changeSet;
};

/**
 * State mutation accumulator. On change type to fwd or swap reset dates
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const setDatesForForward: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    return Boolean(changeSet.productType) &&
        Boolean(accumulatedValue.productType) &&
        changeSet.productType === 'forward' &&
        changeSet.productType !== accumulatedValue.productType ?
        { ...changeSet, date: undefined, dateFar: undefined } :
        changeSet;
};

/**
 * State mutation accumulator. On change type to swap set near date to 'SP'
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const setDatesForSwap: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    return Boolean(changeSet.productType) &&
        Boolean(accumulatedValue.productType) &&
        changeSet.productType === 'swap' &&
        changeSet.productType !== accumulatedValue.productType ?
        { ...changeSet, date: 'SP' as Tenor, dateFar: undefined } :
        changeSet;
};

/**
 * State mutation accumulator. On change type to Pending set expiry type to GTC,
 * PendingType to Limit
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const setParamsForPending: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    return Boolean(changeSet.productType) &&
        Boolean(accumulatedValue.productType) &&
        changeSet.productType === 'pending' &&
        changeSet.productType !== accumulatedValue.productType ?
        {
            ...changeSet,
            expiryType: 'gtc',
            pendingType: 'limit',
            triggerPrice: undefined,
            selectedTradeSide: undefined,
        } :
        changeSet;
};

/**
 * State mutation accumulator. On pending strategy change reset fields
 * @param accumulatedValue previous state
 * @param changeSet new changes
 */
export const setParamsForPendingStrategy: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) => {
    return Boolean(changeSet.pendingType) &&
        Boolean(accumulatedValue.pendingType) &&
        (changeSet.pendingType === 'oco' || changeSet.pendingType === 'ifDone') &&
        changeSet.pendingType !== accumulatedValue.pendingType ?
        {
            ...changeSet,
            selectedTradeSide: undefined,
            selectedTradeSideSecondLeg: undefined,
            priceType: 'limit',
            priceTypeSecondLeg: changeSet.pendingType === 'oco' ? 'stop' : 'limit',
            triggerPrice: undefined,
            triggerPriceSecondLeg: undefined,
            tradedCurrency: Boolean(accumulatedValue.instrument?.symbol) ?
                getCurrenciesFromCcyPair(accumulatedValue.instrument.symbol)?.currencyBase :
                accumulatedValue.tradedCurrency,
            quantity: undefined,
            quantitySecondLeg: undefined,
        } :
        changeSet;
};

const padStateAccumulators: Array<StateMutation<PadConfigExtended>> =
    [
        priceStarted,
        forwardStopOnChanges,
        //TODO: date for SPOT should be separate from forward
        dateForSpot,
        filterInstruments,
        defaultInstrument,
        defaultBaseCurrency,
        updateInstrumentOnInstrumentListChanged,
        resetQtyOnInstrumentChanged,
        resetPendingParamsOnInstrumentChanged,
        resetTradedCcyOnProductChanged,
        setDatesForForward,
        setDatesForSwap,
        setParamsForPending,
        setParamsForPendingStrategy,
    ];

export const rulesAccumulator: StateMutation<PadConfigExtended> = (
    accumulatedValue: PadConfigExtended,
    changeSet: PadConfigExtended,
) =>
    padStateAccumulators.reduce((acc, mut) => mut(accumulatedValue, acc), changeSet);
